import { Application } from "@hotwired/stimulus"
import { identifierForGlobKey } from 'stimulus-vite-helpers'
import { handleStaleChunkError } from '../stale_chunk_reload'

// LAZY Stimulus registry. The usual `registerControllers(app, import.meta.globEager(...))`
// would eagerly import every controller (and its deps — tinymce alone is ~0.5MB) on every
// page. Instead we glob lazily and only import/register a controller the first time its
// identifier actually appears in the DOM. Three triggers cover all arrival paths:
//   1. initial scan (module runs after DOM parse — it's an ESM bundle, so the DOM is ready;
//      NEVER wait on DOMContentLoaded here, it may already have fired),
//   2. turbo:load (Frames/visits — Drive is off app-wide but Frames are still used),
//   3. a MutationObserver, because plenty of markup is injected AFTER load (Vue-rendered
//      lists, jQuery-inserted modals/partials, cocoon nested fields). Without it, a
//      data-controller inside injected HTML would never boot its controller. Stimulus
//      itself picks up new elements for ALREADY-registered identifiers; the observer is
//      only needed to trigger the first lazy import of not-yet-seen ones.
const application = Application.start()
const controllerLoaders = import.meta.glob('./**/*_controller.js')
const registeredControllers = new Set()
const controllerPathsByIdentifier = Object.fromEntries(
  Object.keys(controllerLoaders)
    .map(path => [identifierForGlobKey(path), path])
    .filter(([identifier]) => identifier)
)

function addIdentifiersFromValue(identifiers, value) {
  if (!value) return

  value.split(/\s+/).filter(Boolean).forEach(identifier => identifiers.add(identifier))
}

function controllerIdentifiersIn(element) {
  const identifiers = new Set()

  if (element.dataset) addIdentifiersFromValue(identifiers, element.dataset.controller)

  element.querySelectorAll('[data-controller]').forEach(controllerElement => {
    addIdentifiersFromValue(identifiers, controllerElement.dataset.controller)
  })

  return identifiers
}

// ★★ A lazily-imported controller chunk CAN fail to load, and when it does the damage is silent.
// The common cause is a deploy: Vite fingerprints every chunk, so a tab opened before the deploy
// asks for `submit_on_enter_controller.7d3db306.js`, which no longer exists, and the import
// rejects. PostHog caught exactly that on /inventory/:slug/edit (submit_on_enter, tinymce and
// ai_description) once client-side exception capture was switched on.
//
// Two separate bugs lived in the old three lines:
//   1. nothing caught the rejection, so the controller never registered and that part of the form
//      simply stopped working — no error, nothing for a dealer to act on. They click, nothing
//      happens, they click again. That is what the rage-click data on this page actually is.
//   2. the identifier was added to `registeredControllers` BEFORE the await, so a FAILED import
//      still marked it as registered — permanently. No later turbo:load and no MutationObserver
//      sighting would ever retry it. One blocked request killed that controller for the life of
//      the page.
// Now a failure rolls the mark back so the next sighting genuinely retries, and a chunk that is
// really gone (a deploy moved it) is handed to `handleStaleChunkError`
// (javascripts/stale_chunk_reload.js), which owns BOTH cures — the guarded one-shot reload and the
// "a new version was released" bar. Keep that decision in one place: this file used to carry its
// own copy of the regex + the bar, and two copies of a rule this subtle will drift.
const failedControllerAttempts = new Map()

function reportControllerFailure(identifier, error) {
  try {
    if (window.posthog && typeof window.posthog.captureException === 'function') {
      window.posthog.captureException(error, {
        controller: identifier,
        reason: 'controller_chunk_load_failed'
      })
    }
  } catch (e) { /* telemetry must never be the thing that breaks the page */ }
  try { console.error('[stimulus] controller failed to load:', identifier, error) } catch (e) {}
}

async function registerController(identifier) {
  if (registeredControllers.has(identifier)) return

  const controllerPath = controllerPathsByIdentifier[identifier]
  if (!controllerPath) return

  // Mark BEFORE the (async) import so a second sighting during the await can't
  // double-register the same identifier.
  registeredControllers.add(identifier)

  let controllerModule
  try {
    controllerModule = await controllerLoaders[controllerPath]()
  } catch (error) {
    // ★ Roll the mark back, or this identifier is dead for the rest of the page's life.
    registeredControllers.delete(identifier)

    const attempts = (failedControllerAttempts.get(identifier) || 0) + 1
    failedControllerAttempts.set(identifier, attempts)
    reportControllerFailure(identifier, error)

    // ★ 2026-09-24: hand every failure to the shared stale-chunk handler, which reloads the page
    // ONCE (loop-guarded in sessionStorage) when the message says the chunk is simply gone — the
    // deploy case, which is 12 of 23 client-side exceptions. It ignores anything that is not a
    // stale-chunk message, so an error thrown INSIDE a controller module never reloads anything.
    // `notice` keeps this file's older rule: one failure can be a dropped request, so only a
    // SECOND failure on the same controller is allowed to blame a new release on screen (which is
    // what the dealer sees when a reload was refused — private mode, mid-typing, already reloaded).
    handleStaleChunkError(error, { source: `stimulus:${identifier}`, notice: attempts >= 2 })
    return
  }

  if (typeof controllerModule.default === 'function') {
    application.register(identifier, controllerModule.default)
  }
}

function loadControllersIn(element = document) {
  controllerIdentifiersIn(element).forEach(identifier => {
    registerController(identifier)
  })
}

loadControllersIn()
document.addEventListener('turbo:load', () => loadControllersIn())

new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) loadControllersIn(node)
    })
  })
}).observe(document.documentElement, { childList: true, subtree: true })
